/**
 * The Emails page: how sending is going first, then bulk sends (one email
 * to a segment), the templates, the brands they send as, and the
 * never-email list. A bulk send is an automation underneath, listed here
 * rather than on the Automations page; "New bulk send" makes one.
 *
 * The tab is in the URL (`?tab=`), so a link can open straight to one.
 */
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ActionIcon, Alert, Anchor, Button, Group, Loader, Menu, Modal, SegmentedControl, Stack, Table, Tabs, Text, TextInput, Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCopy, IconDots, IconMail, IconPencil, IconPlus, IconSend, IconTrash } from '@tabler/icons-react';
import {
  useCreateEmailTemplate, useDeleteEmailTemplate, useEmailTemplates, useRenameEmailTemplate,
} from '../api/queries';
import { useWorkflowsUsedBy } from '../api/workflows';
import { formatDateTime } from '../theme';
import { OverviewTab } from './emails/OverviewTab';
import { BrandsTab } from './emails/BrandsTab';
import { BulkSendsTab } from './emails/BulkSendsTab';
import { UnsubscribesTab } from './emails/UnsubscribesTab';
import { useVocab } from '../vocab';

const TABS = ['overview', 'bulk', 'templates', 'brands', 'unsubscribes'] as const;

export function EmailsPage() {
  const [params, setParams] = useSearchParams();
  const tab = (TABS as readonly string[]).includes(params.get('tab') ?? '') ? params.get('tab')! : 'overview';
  const range = params.get('range') ?? '30d';
  const go = (t: string | null, extra: Record<string, string> = {}) =>
    setParams((p) => {
      const next = new URLSearchParams(p);
      next.set('tab', t ?? 'overview');
      Object.entries(extra).forEach(([k, v]) => next.set(k, v));
      return next;
    }, { replace: true });

  return (
    <Stack gap="sm">
      <Group justify="space-between">
        <Title order={2}>Emails</Title>
        <Button
          component={Link}
          to="/emails/bulk/new"
          leftSection={<IconSend size={16} />}
          data-testid="email-a-list"
        >
          New bulk send
        </Button>
      </Group>
      <Tabs value={tab} onChange={(t) => go(t)} keepMounted={false}>
        <Tabs.List mb="md">
          <Tabs.Tab value="overview">Overview</Tabs.Tab>
          <Tabs.Tab value="bulk">Bulk sends</Tabs.Tab>
          <Tabs.Tab value="templates">Templates</Tabs.Tab>
          <Tabs.Tab value="brands">Brands</Tabs.Tab>
          <Tabs.Tab value="unsubscribes">Never email</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="overview"><OverviewTab range={range} onRange={(r) => go('overview', { range: r })} /></Tabs.Panel>
        <Tabs.Panel value="bulk"><BulkSendsTab /></Tabs.Panel>
        <Tabs.Panel value="templates"><TemplatesTab /></Tabs.Panel>
        <Tabs.Panel value="brands"><BrandsTab /></Tabs.Panel>
        <Tabs.Panel value="unsubscribes"><UnsubscribesTab /></Tabs.Panel>
      </Tabs>
    </Stack>
  );
}

function TemplatesTab() {
  const v = useVocab();
  const templates = useEmailTemplates();
  const create = useCreateEmailTemplate();
  const remove = useDeleteEmailTemplate();
  const rename = useRenameEmailTemplate();
  const navigate = useNavigate();
  const [naming, setNaming] = useState<{ copyFrom?: string; name: string; kind?: 'design' | 'html' } | null>(null);
  const [removing, setRemoving] = useState<{ id: string; name: string } | null>(null);
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(null);
  const list = templates.data?.templates ?? [];

  async function make() {
    if (!naming?.name.trim()) return;
    try {
      const made = await create.mutateAsync({
        name: naming.name.trim(),
        copy_from: naming.copyFrom,
        kind: naming.copyFrom ? undefined : (naming.kind ?? 'design'),
      });
      setNaming(null);
      navigate(`/emails/${made.id}`);
    } catch (e) {
      notifications.show({ color: 'red', title: 'Could not create the template', message: (e as Error).message });
    }
  }

  async function saveName() {
    const name = renaming?.name.trim();
    if (!renaming || !name) return;
    try {
      await rename.mutateAsync({ id: renaming.id, name });
      setRenaming(null);
    } catch (e) {
      notifications.show({ color: 'red', title: 'Could not rename', message: (e as Error).message });
    }
  }

  return (
    <Stack gap="sm">
      <Group justify="space-between">
        <Text size="sm" c="dimmed" maw={640}>
          {`Design an email once and send it from any ${v.one('workflow')}. Build it by dragging blocks, or paste your own HTML. Put ${v.a('lead')}'s details in with tags such as `}
          <code>{'{{firstname}}'}</code>.
        </Text>
        <Button variant="default" leftSection={<IconPlus size={16} />} onClick={() => setNaming({ name: '' })}>
          New template
        </Button>
      </Group>

      {templates.error && <Alert color="red" variant="light">{templates.error.message}</Alert>}

      <Table.ScrollContainer minWidth={560}>
        <Table highlightOnHover verticalSpacing={8}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Template</Table.Th>
              <Table.Th w={200}>Edited by</Table.Th>
              <Table.Th w={160}>Last edited</Table.Th>
              <Table.Th w={40} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {templates.isPending && (
              <Table.Tr><Table.Td colSpan={4}><Group justify="center" py="lg"><Loader size="sm" /></Group></Table.Td></Table.Tr>
            )}
            {!templates.isPending && list.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={4}>
                  <Stack align="center" gap={6} py="xl">
                    <IconMail size={28} stroke={1.5} color="var(--mantine-color-dimmed)" />
                    <Text size="sm" c="dimmed">No templates yet.</Text>
                    <Button size="xs" variant="light" onClick={() => setNaming({ name: '' })}>
                      Make the first one
                    </Button>
                  </Stack>
                </Table.Td>
              </Table.Tr>
            )}
            {list.map((t) => (
              <Table.Tr key={t.id}>
                <Table.Td>
                  <Link to={`/emails/${t.id}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                    <Text size="sm" fw={600}>{t.name}</Text>
                    <Text size="xs" c="dimmed" lineClamp={1}>{t.subject || 'No subject yet'}</Text>
                  </Link>
                </Table.Td>
                <Table.Td><Text size="sm" c="dimmed" truncate>{t.created_by || '-'}</Text></Table.Td>
                <Table.Td><Text size="sm" c="dimmed">{formatDateTime(t.updated_at)}</Text></Table.Td>
                <Table.Td>
                  <Menu position="bottom-end" withinPortal>
                    <Menu.Target>
                      <ActionIcon variant="subtle" color="gray" aria-label={`More for ${t.name}`}>
                        <IconDots size={16} />
                      </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Item
                        leftSection={<IconPencil size={14} />}
                        onClick={() => setRenaming({ id: t.id, name: t.name })}
                      >
                        Rename
                      </Menu.Item>
                      <Menu.Item
                        leftSection={<IconCopy size={14} />}
                        onClick={() => setNaming({ copyFrom: t.id, name: `${t.name} (copy)` })}
                      >
                        Make a copy
                      </Menu.Item>
                      <Menu.Item
                        color="red"
                        leftSection={<IconTrash size={14} />}
                        onClick={() => setRemoving({ id: t.id, name: t.name })}
                      >
                        Delete
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      <Modal
        opened={naming !== null}
        onClose={() => setNaming(null)}
        title={naming?.copyFrom ? 'Copy template' : 'New template'}
        size="sm"
      >
        <Stack>
          <TextInput
            label="Name"
            description="Only you and your team see this"
            placeholder="Welcome to Swicc"
            data-autofocus
            value={naming?.name ?? ''}
            onChange={(e) => { const name = e.currentTarget.value; setNaming((n) => (n ? { ...n, name } : n)); }}
            onKeyDown={(e) => { if (e.key === 'Enter') void make(); }}
          />
          {!naming?.copyFrom && (
            <Stack gap={4}>
              <Text size="sm" fw={500}>Build it with</Text>
              <SegmentedControl
                value={naming?.kind ?? 'design'}
                onChange={(v) => setNaming((n) => (n ? { ...n, kind: v as 'design' | 'html' } : n))}
                data={[
                  { value: 'design', label: 'Drag and drop' },
                  { value: 'html', label: 'Your own HTML' },
                ]}
              />
              <Text size="xs" c="dimmed">You can switch later.</Text>
            </Stack>
          )}
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setNaming(null)}>Cancel</Button>
            <Button loading={create.isPending} disabled={!naming?.name.trim()} onClick={() => void make()}>
              {naming?.copyFrom ? 'Make copy' : 'Create'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal opened={renaming !== null} onClose={() => setRenaming(null)} title="Rename template" size="sm">
        <Stack>
          <TextInput
            label="Name"
            description="Only you and your team see this"
            data-autofocus
            value={renaming?.name ?? ''}
            onChange={(e) => { const name = e.currentTarget.value; setRenaming((r) => (r ? { ...r, name } : r)); }}
            onKeyDown={(e) => { if (e.key === 'Enter') void saveName(); }}
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setRenaming(null)}>Cancel</Button>
            <Button loading={rename.isPending} disabled={!renaming?.name.trim()} onClick={() => void saveName()}>
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>

      {removing && (
        <DeleteTemplateModal
          template={removing}
          pending={remove.isPending}
          onClose={() => setRemoving(null)}
          onDelete={() =>
            remove.mutate(removing.id, {
              onSuccess: () => setRemoving(null),
              onError: (e) => notifications.show({ color: 'red', title: 'Could not delete', message: (e as Error).message }),
            })
          }
        />
      )}
    </Stack>
  );
}

/**
 * Deleting a template: first, who sends it. A switched-on workflow that
 * sends it blocks the delete (the server refuses too) and is named; one
 * that is off or archived is a warning.
 */
export function DeleteTemplateModal({
  template, pending, onClose, onDelete,
}: { template: { id: string; name: string }; pending: boolean; onClose: () => void; onDelete: () => void }) {
  const v = useVocab();
  const used = useWorkflowsUsedBy('email_template', template.id);
  const d = used.data;
  const blocking = (d?.workflows ?? []).filter((w) => w.enabled && !w.archived);
  const others = [
    ...(d?.workflows ?? []).filter((w) => !w.enabled || w.archived).map((w) => w.name),
  ];
  const quote = (names: string[]) => names.map((n) => `'${n}'`).join(', ');
  return (
    <Modal opened onClose={onClose} title="Delete template?" size="sm">
      <Stack>
        {used.isPending && <Loader size="xs" />}
        {blocking.length > 0 ? (
          <Alert color="red" variant="light" data-testid="template-delete-blocked">
            <Text size="sm">
              {`${blocking.length} active ${blocking.length === 1 ? `${v.one('workflow')} sends` : `${v.many('workflow')} send`} this template: ${quote(blocking.map((w) => w.name))}. Switch ${blocking.length === 1 ? 'it' : 'them'} off or change ${blocking.length === 1 ? 'it' : 'them'} on the ${v.Many('workflow')} page before deleting.`}
            </Text>
            <Group gap="sm" mt={6}>
              {blocking.map((w) => (
                <Anchor key={w.id} component={Link} to={`/automations/${encodeURIComponent(w.id)}`} size="sm" c="inherit" fw={600} underline="always">
                  {`Open '${w.name}'`}
                </Anchor>
              ))}
            </Group>
          </Alert>
        ) : (
          <Text size="sm">
            <b>{template.name}</b> will be deleted. This cannot be undone.
          </Text>
        )}
        {others.length > 0 && (
          <Alert color="chartAmber" variant="light" data-testid="template-delete-warning">
            <Text size="sm">{`Also used by ${quote(others)}. They will fail if they send it after it is gone.`}</Text>
          </Alert>
        )}
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>Cancel</Button>
          <Button color="red" loading={pending} disabled={blocking.length > 0 || used.isPending} onClick={onDelete} data-testid="confirm-delete-template">
            Delete
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
